import { getAssociatedTokenAddressSync } from '@solana/spl-token';
import type { Connection, PublicKey } from '@solana/web3.js';
import type BN from 'bn.js';
import { fetchMarket } from '../accounts/fetch.js';
import { estimateComputeUnits } from '../constants/cu-table.js';
import { MORFI_OPINION_PROGRAM_ID, TOKEN_PROGRAM_ID } from '../constants/programs.js';
import type { BuildParams, BuildResult } from '../types/build-result.js';
import {
  type BackingMarketAccounts,
  backingPda,
  buildBackInstruction,
  buildUnbackInstruction,
} from './backing-instruction.js';
import { createBuildResult, createIdempotentAtaIx, uniqueSignerKeys } from './common.js';

interface BackingParams extends BuildParams, Partial<BackingMarketAccounts> {
  backer: PublicKey;
  market: PublicKey;
}

export interface BackParams extends BackingParams {
  /** Principal deposited into prize liability (D6); backing fee is charged on top. */
  amount: BN;
  /** Rent payer for idempotent ATA creations; defaults to backer. */
  payer?: PublicKey;
}

export interface UnbackParams extends BackingParams {
  /** Shares burned (D6); unbacking fee is deducted from the quote payout. */
  burnAmount: BN;
}

/**
 * Build a back transaction with optional ATA rent sponsorship. Supplying marketConfig,
 * quoteMint and eventSchedule avoids the existing Market read. The pinned contract
 * creates the share mint at market creation, so both ATAs can be prepared beforehand.
 */
export async function buildBack(connection: Connection, params: BackParams): Promise<BuildResult> {
  const accounts = await resolveBackingAccounts(connection, params);
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const shareMint = backingPda(programId, 'backer_share_mint', params.market.toBuffer());
  const ataPayer = params.payer ?? params.backer;
  const instruction = await buildBackInstruction(connection, { ...params, ...accounts });
  return createBuildResult(
    [
      createIdempotentAtaIx(ataPayer, params.backer, accounts.quoteMint, TOKEN_PROGRAM_ID),
      createIdempotentAtaIx(ataPayer, params.backer, shareMint, TOKEN_PROGRAM_ID),
      instruction,
    ],
    uniqueSignerKeys(params.backer, ataPayer),
    params.computeUnits ?? estimateComputeUnits('back'),
  );
}

/**
 * Build an unback transaction that burns fee shares and refunds quote less the inline
 * unbacking fee. Supplying all three market bindings avoids the existing Market read.
 */
export async function buildUnback(
  connection: Connection,
  params: UnbackParams,
): Promise<BuildResult> {
  const accounts = await resolveBackingAccounts(connection, params);
  const instruction = await buildUnbackInstruction(connection, { ...params, ...accounts });
  return createBuildResult(
    [instruction],
    [params.backer],
    params.computeUnits ?? estimateComputeUnits('unback'),
  );
}

/** Derive the classic SPL share ATA without an account read. */
export function deriveBackerShareAta(
  backer: PublicKey,
  market: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): PublicKey {
  const mint = backingPda(programId, 'backer_share_mint', market.toBuffer());
  return getAssociatedTokenAddressSync(mint, backer, true);
}

async function resolveBackingAccounts(
  connection: Connection,
  params: BackingParams,
): Promise<BackingMarketAccounts> {
  let { marketConfig, quoteMint, eventSchedule } = params;
  if (marketConfig === undefined || quoteMint === undefined || eventSchedule === undefined) {
    const market = await fetchMarket(connection, params.market, params.programId);
    marketConfig ??= market.config;
    quoteMint ??= market.quoteMint;
    if (eventSchedule === undefined) eventSchedule = market.eventSchedule;
  }
  return { marketConfig, quoteMint, eventSchedule };
}
